import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { t as i18nT } from '@/i18n'

export type WaybillPackingFieldKey =
  | 'containerType'
  | 'containerNo'
  | 'palletNo'
  | 'sealNo'
  | 'pieces'
  | 'grossWeight'
  | 'volume'
  | 'goodsNameEn'
  | 'marks'
  | 'cartonNos'
  | 'remark'

export interface WaybillPackingFieldConfig {
  key: WaybillPackingFieldKey
  label: string
  required?: boolean
}

export interface WaybillPackingExcelRow {
  containerType?: string | null
  containerNo?: string | null
  palletNo?: string | null
  sealNo?: string | null
  pieces?: number | null
  grossWeight?: number | null
  volume?: number | null
  goodsNameEn?: string | null
  marks?: string | null
  cartonNos?: string | null
  remark?: string | null
}

const TRANSPORT_MODE_SEA_FCL = 1
const TRANSPORT_MODE_SEA_LCL = 2
const TRANSPORT_MODE_AIR = 3
const TRANSPORT_MODE_LAND = 4
const TRANSPORT_MODE_RAIL = 5
const TRANSPORT_MODE_EXPRESS = 6

const BASIC_CARGO_PACKING_FIELD_DEFS: Array<Pick<WaybillPackingFieldConfig, 'key' | 'required'>> = [
  { key: 'pieces', required: true },
  { key: 'grossWeight', required: true },
  { key: 'volume', required: true }
]

const COMMON_CARGO_PACKING_FIELD_DEFS: Array<Pick<WaybillPackingFieldConfig, 'key' | 'required'>> = [
  { key: 'sealNo', required: true },
  ...BASIC_CARGO_PACKING_FIELD_DEFS,
  { key: 'goodsNameEn' },
  { key: 'marks' },
  { key: 'cartonNos' }
]

const CONTAINER_PACKING_MODES = [
  TRANSPORT_MODE_SEA_FCL,
  TRANSPORT_MODE_SEA_LCL,
  TRANSPORT_MODE_LAND,
  TRANSPORT_MODE_RAIL
]

const NUMERIC_FIELDS = new Set<WaybillPackingFieldKey>(['pieces', 'grossWeight', 'volume'])

const HEADER_ALIASES: Record<string, WaybillPackingFieldKey> = {
  集装箱箱型: 'containerType',
  箱型: 'containerType',
  集装箱柜号: 'containerNo',
  柜号: 'containerNo',
  箱号: 'containerNo',
  栈板号: 'palletNo',
  托板号: 'palletNo',
  托盘号: 'palletNo',
  封条号: 'sealNo',
  封号: 'sealNo',
  纸箱数: 'pieces',
  件数: 'pieces',
  '毛重(KGS)': 'grossWeight',
  毛重: 'grossWeight',
  '体积(CBM)': 'volume',
  体积: 'volume',
  英文品名: 'goodsNameEn',
  唛头: 'marks',
  纸箱号: 'cartonNos',
  备注: 'remark'
}

export function getWaybillPackingFieldConfigsByMode(mode: number): WaybillPackingFieldConfig[] {
  const basicFields = BASIC_CARGO_PACKING_FIELD_DEFS.map(createWaybillPackingFieldConfig)
  const commonFields = COMMON_CARGO_PACKING_FIELD_DEFS.map(createWaybillPackingFieldConfig)
  if (CONTAINER_PACKING_MODES.includes(mode)) {
    return [
      createWaybillPackingFieldConfig({ key: 'containerType', required: true }),
      createWaybillPackingFieldConfig({ key: 'containerNo', required: true }),
      ...commonFields
    ]
  }
  if (mode === TRANSPORT_MODE_AIR) {
    return [
      createWaybillPackingFieldConfig({ key: 'palletNo' }),
      ...commonFields
    ]
  }
  if (mode === TRANSPORT_MODE_EXPRESS) {
    return basicFields
  }
  return basicFields
}

export function getWaybillPackingFieldKeysByMode(mode: number): Set<WaybillPackingFieldKey> {
  return new Set(getWaybillPackingFieldConfigsByMode(mode).map(field => field.key))
}

export async function downloadWaybillPackingTemplate(
  mainTransportMode: number,
  filename = getWaybillPackingText('waybill.detail.packingTemplate.fileName'),
  fieldConfigs?: WaybillPackingFieldConfig[]
): Promise<void> {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(getWaybillPackingText('waybill.detail.packingTemplate.sheetName'))
  // 允许独立业务页面传入段落化字段；其他调用方仍沿用运输方式默认配置。
  const columns = fieldConfigs || getWaybillPackingFieldConfigsByMode(mainTransportMode)
  worksheet.columns = columns.map(field => ({
    header: field.required ? `${field.label}*` : field.label,
    key: field.key,
    width: Math.max(14, field.label.length * 2 + 6)
  }))
  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' }
  worksheet.getRow(1).height = 22
  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  )
}

export async function parseWaybillPackingExcel(
  file: File,
  mainTransportMode: number
): Promise<WaybillPackingExcelRow[]> {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(await file.arrayBuffer())
  const worksheet = workbook.worksheets[0]
  if (!worksheet) {
    throw new Error(getWaybillPackingText('waybill.messages.packingWorksheetMissing'))
  }

  const enabledFields = getWaybillPackingFieldKeysByMode(mainTransportMode)
  const headerMap = new Map<number, WaybillPackingFieldKey>()
  worksheet.getRow(1).eachCell((cell, colNumber) => {
    const field = resolveHeaderField(getCellText(cell))
    if (field && enabledFields.has(field)) {
      headerMap.set(colNumber, field)
    }
  })
  if (headerMap.size === 0) {
    throw new Error(getWaybillPackingText('waybill.messages.packingHeaderMissing'))
  }

  const rows: WaybillPackingExcelRow[] = []
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const next: WaybillPackingExcelRow = {}
    row.eachCell((cell, colNumber) => {
      const field = headerMap.get(colNumber)
      if (!field) return
      const text = getCellText(cell)
      setPackingFieldValue(next, field, NUMERIC_FIELDS.has(field) ? toPackingNumber(text, field) : text)
    })
    if (hasPackingValue(next)) {
      rows.push(normalizePackingRow(next, enabledFields))
    }
  })

  if (rows.length === 0) {
    throw new Error(getWaybillPackingText('waybill.messages.packingNoData'))
  }
  return rows
}

function createWaybillPackingFieldConfig(
  definition: Pick<WaybillPackingFieldConfig, 'key' | 'required'>
): WaybillPackingFieldConfig {
  return {
    ...definition,
    label: getWaybillPackingText(`waybill.detail.packingFields.${definition.key}`)
  }
}

function getWaybillPackingText(key: string): string {
  const value = i18nT(key)
  return value && value !== key ? value : key
}

function resolveHeaderField(header: string): WaybillPackingFieldKey | null {
  const normalized = header.replace(/\*/g, '').replace(/\s+/g, '').trim()
  return HEADER_ALIASES[normalized] || null
}

function getCellText(cell: ExcelJS.Cell): string {
  const value = cell.value as any
  if (value === null || value === undefined) return ''
  if (typeof value === 'object' && 'text' in value) return String(value.text || '').trim()
  if (typeof value === 'object' && 'result' in value) return String(value.result || '').trim()
  if (typeof value === 'object' && 'richText' in value && Array.isArray(value.richText)) {
    return value.richText.map((item: { text?: string }) => item.text || '').join('').trim()
  }
  return String(value).trim()
}

function toPackingNumber(value: unknown, field: WaybillPackingFieldKey): number | null {
  const text = String(value ?? '').replace(/,/g, '').trim()
  if (!text) return null
  const numberValue = Number(text)
  if (!Number.isFinite(numberValue)) return null
  if (field === 'pieces') return Math.trunc(numberValue)
  if (field === 'grossWeight') return Number(numberValue.toFixed(2))
  if (field === 'volume') return Number(numberValue.toFixed(3))
  return numberValue
}

function setPackingFieldValue(
  row: WaybillPackingExcelRow,
  field: WaybillPackingFieldKey,
  value: string | number | null
): void {
  switch (field) {
    case 'pieces':
    case 'grossWeight':
    case 'volume':
      row[field] = typeof value === 'number' ? value : null
      break
    default:
      row[field] = value === null ? null : String(value)
      break
  }
}

function hasPackingValue(row: WaybillPackingExcelRow): boolean {
  return Object.values(row).some(value => value !== undefined && value !== null && value !== '')
}

function normalizePackingRow(
  row: WaybillPackingExcelRow,
  enabledFields: Set<WaybillPackingFieldKey>
): WaybillPackingExcelRow {
  const next: WaybillPackingExcelRow = {}
  enabledFields.forEach(field => {
    setPackingFieldValue(next, field, row[field] ?? null)
  })
  return next
}
